import { errorToast } from "@/v2-ui/toast"
import {
  ConfirmButton,
  TooltipButton,
} from "@/v2-backoffice-client/components/button"
import { useTenantCustomerAssignMutation } from "@/v2-backoffice-client/tenant/customer/add/tenant.customer.assign.mutation"
import { useUserCustomerUpdateMutation } from "@/v2-backoffice-client/user/customer/update/user.customer.update.mutation"
import {
  formatMismatchCustomerName,
  getMismatchChoices,
  type MismatchChoice,
} from "@/v2-backoffice-client/user/customerMismatch/userCustomerMismatch.utils"
import type { UserCustomerMismatchFragment } from "@/v2-backoffice-client/user/customerMismatch/__types__/user.customerMismatch.fragment"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"

export type UserCustomerMismatchResolveCellProps = {
  mismatch: UserCustomerMismatchFragment
  /** The row is gone once it is put right; the board refetches. */
  onResolved?(): void
}

/**
 * The Put Right cell: the row's choices as buttons, side by side, each a
 * confirm away. A mismatch has two ways out and the cell shows both -
 * "User → X" moves the user to where the team belongs, "Team → Y" moves
 * the team (every member) to where the user belongs - with the one the
 * team's other members point at filled in and the other outlined. When
 * HubSpot merged both customers' companies into one, there is one way and
 * one button: "Both → Z" (or just the side that still has to move). While
 * a merged-away side's survivor has no customer yet, the cell says so and
 * nothing can be clicked.
 */
function UserCustomerMismatchResolveCell(
  props: UserCustomerMismatchResolveCellProps,
) {
  const { mismatch, onResolved } = props
  const { User: user, Tenant: tenant } = mismatch

  const userName = formatUserName(user?.FirstName, user?.LastName)
  const teamName = tenant?.Name ?? `#${mismatch.TenantId}`
  const members = `${mismatch.TeamMemberCount} member${mismatch.TeamMemberCount === 1 ? "" : "s"}`
  const userCustomerName = formatMismatchCustomerName(
    mismatch.UserCustomerName,
    mismatch.UserCustomerExternalId,
  )
  const teamCustomerName = formatMismatchCustomerName(
    mismatch.TeamCustomerName,
    mismatch.TeamCustomerExternalId,
  )

  // Each write resolves with its data only when it went through
  // (errorPolicy is "all" app-wide: a rejected write resolves with the
  // error and no data instead of throwing), so the row is reported as put
  // right only then.
  const [updateUser, { loading: updatingUser, error: userError }] =
    useUserCustomerUpdateMutation()
  const [assignTeam, { loading: assigningTeam, error: teamError }] =
    useTenantCustomerAssignMutation()
  errorToast(userError, { title: "Failed to update the user's customer" })
  errorToast(teamError, { title: "Failed to update the team's customer" })

  async function moveUserTo(customerId: number) {
    const result = await updateUser({
      variables: { id: mismatch.UserId, customerId },
    })
    return Boolean(result.data?.updateUser)
  }

  async function moveTeamTo(externalCustomerId: string) {
    const result = await assignTeam({
      variables: { tenantId: mismatch.TenantId, externalCustomerId },
    })
    return Boolean(result.data?.assignCustomerToTenant)
  }

  const choices = getMismatchChoices(mismatch)

  if (choices.kind === "pending") {
    return (
      <TooltipButton
        size="xs"
        variant="outline"
        iconSrc="hourglass"
        disabled
        content={`HubSpot merged a customer's company into company ${choices.survivingExternalCompanyId}, which has no customer yet. The sync links one within the hour; nothing can move until then.`}
      >
        Waiting for the sync
      </TooltipButton>
    )
  }

  if (choices.kind === "single") {
    const { target, moveUser, moveTeam, mergedNames } = choices
    const what =
      moveUser && moveTeam
        ? `${userName} and the team ${teamName} (${members})`
        : moveTeam
          ? `the team ${teamName} and all its ${members}`
          : userName
    const label = moveUser && moveTeam ? "Both" : moveTeam ? "Team" : "User"
    const merged =
      mergedNames.length > 0
        ? `HubSpot merged ${mergedNames.join(" and ")} into ${target.Name}, so both sides of this row are that one customer.`
        : ""
    return (
      <ConfirmButton
        size="xs"
        variant="suggested"
        iconSrc="shuffle"
        isLoading={updatingUser || assigningTeam}
        disabled={moveTeam && !target.ExternalCustomerId}
        content={`${merged} Move ${what} there; licenses and seats follow.`}
        confirmContent={`Move ${what} to ${target.Name}?`}
        confirmVariant="suggested"
        onConfirm={async () => {
          // The team first, then the user, so the user always ends up on
          // the customer of the team they sit in; either write failing
          // leaves the row on the board for the next look.
          if (moveTeam) {
            if (!target.ExternalCustomerId) return
            if (!(await moveTeamTo(target.ExternalCustomerId))) return
          }
          if (moveUser && !(await moveUserTo(target.Id))) return
          onResolved?.()
        }}
      >
        {label} → {target.Name}
      </ConfirmButton>
    )
  }

  function renderChoice(choice: MismatchChoice) {
    const isUser = choice.moves === "user"
    const mergedNote = isUser
      ? mismatch.TeamCustomerSurvivingExternalCompanyId
        ? ` HubSpot merged the team's customer ${teamCustomerName} into it.`
        : ""
      : mismatch.UserCustomerSurvivingExternalCompanyId
        ? ` HubSpot merged the user's customer ${userCustomerName} into it.`
        : ""
    const content = isUser
      ? `Move ${userName} to ${choice.target.Name}, the customer the team belongs to. Their licenses and seats follow.${mergedNote}`
      : `Move the team ${teamName} and all its ${members} to ${choice.target.Name}, the customer that owns the user.${mergedNote}`
    const suggested = choice.isSuggested
      ? " The team's other members point this way."
      : ""
    return (
      <ConfirmButton
        size="xs"
        variant={choice.isSuggested ? "suggested" : "outline"}
        iconSrc={isUser ? "user" : "users"}
        isLoading={isUser ? updatingUser : assigningTeam}
        disabled={Boolean(choice.disabledReason)}
        content={choice.disabledReason ?? `${content}${suggested}`}
        confirmContent={
          isUser
            ? `Move ${userName} to ${choice.target.Name}?${mergedNote}`
            : `Move the team ${teamName} and all its ${members} to ${choice.target.Name}?${mergedNote}`
        }
        confirmVariant="suggested"
        onConfirm={async () => {
          const done = isUser
            ? await moveUserTo(choice.target.Id)
            : choice.target.ExternalCustomerId
              ? await moveTeamTo(choice.target.ExternalCustomerId)
              : false
          if (done) onResolved?.()
        }}
      >
        {isUser ? "User" : "Team"} → {choice.target.Name}
      </ConfirmButton>
    )
  }

  return (
    <span className="flex flex-wrap items-center gap-1.5 [&>*]:shrink-0">
      {renderChoice(choices.user)}
      {renderChoice(choices.team)}
    </span>
  )
}

export default UserCustomerMismatchResolveCell
